import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { getClientTz, shortDate } from './date';
import { faFolderGear, faMessages, faRobot, faUser } from '@fortawesome/pro-solid-svg-icons';
import { faNotebook } from '@fortawesome/pro-regular-svg-icons';

export const resourceTypesOptions = [
    { label: 'Message Hub', value: 'user' },
    { label: 'Campaign', value: 'campaign' },
    { label: 'Automation', value: 'automation' },
    { label: 'Playbook', value: 'playbook' },
    { label: 'Other', value: 'other' },
];

export const resourceTypeIcon = (resourceType) => {
    const icons = {
        user: faUser,
        campaign: faMessages,
        automation: faRobot,
        playbook: faNotebook,
    };

    return icons[resourceType] || faFolderGear;
};

/** @param {string[]} list */
export const generateLegendColors = (list) => {
    const fixedColors = [
        {
            iconColor: '#3B82F6', // blue
            bgColor: '#93C5FD',
            labels: ['Marketing', 'marketing'],
        },
        {
            iconColor: '#D946EF', // fuchsia
            bgColor: '#F0ABFC',
            labels: ['Transactional', 'transactional'],
        },
        {
            iconColor: '#F43F5E', // rose
            bgColor: '#FDA4AF',
            labels: ['Acquisition', 'acquisition'],
        },
        {
            iconColor: '#F59E0B', // amber
            bgColor: '#FCD34D',
            labels: ['Re-acquisition', 're_acquisition'],
        },
    ];

    const availableColors = [
        {
            // blue
            iconColor: '#3B82F6',
            bgColor: '#93C5FD',
        },
        {
            // fuchsia
            iconColor: '#D946EF',
            bgColor: '#F0ABFC',
        },
        {
            // rose
            iconColor: '#F43F5E',
            bgColor: '#FDA4AF',
        },
        {
            // amber
            iconColor: '#F59E0B',
            bgColor: '#FCD34D',
        },
        {
            // lime
            iconColor: '#84CC16',
            bgColor: '#BEF264',
        },
        {
            // violet
            iconColor: '#8B5CF6',
            bgColor: '#C4B5FD',
        },
        {
            // sky
            iconColor: '#14ABE0',
            bgColor: '#73D8FC',
        },

        {
            // pink
            iconColor: '#EC4899',
            bgColor: '#F9A8D4',
        },
        {
            // green
            iconColor: '#22c55e',
            bgColor: '#86efac',
        },

        {
            // orange
            iconColor: '#F97316',
            bgColor: '#FDBA74',
        },

        {
            // blue gray
            iconColor: '#64748B',
            bgColor: '#CBD5E1',
        },
        {
            // cyan
            iconColor: '#06b6d4',
            bgColor: '#67e8f9',
        },
        {
            // slate
            iconColor: '#64748b',
            bgColor: '#cbd5e1',
        },
    ];

    const usedColors = new Set();

    return list.map((item) => {
        const fixedColor = fixedColors.find((color) => color.labels.includes(item) && !usedColors.has(color.iconColor));

        if (fixedColor) {
            usedColors.add(fixedColor.iconColor);
            return { label: item, iconColor: fixedColor.iconColor, bgColor: fixedColor.bgColor };
        }

        // to avoid duplicate colors from fixed it needs to
        // find the first unused color from availableColors
        const color = availableColors.find((color) => !usedColors.has(color.iconColor));

        if (color) {
            usedColors.add(color.iconColor);
            return { label: item, iconColor: color.iconColor, bgColor: color.bgColor };
        }

        return {
            label: item,
            iconColor: '#000',
            bgColor: '#ccc',
        };
    });
};

export const minAllowedDate = dayjs.utc('2024-07-01 04:00:00');
export const minAllowedComparisonDate = dayjs.utc('2024-06-01 04:00:00');

/**
 * Returns the percentage difference between two numbers.
 *
 * @param {number[]} numbers
 * @returns {number}
 */
export const calculateDifference = (numbers) => {
    const difference = numbers[0] !== 0 ? (numbers[1] - numbers[0]) / numbers[0] : null;

    return difference;
};

export const getCurrentDate = () => {
    return dayjs().tz(getClientTz()).subtract(30, 'minutes');
};

export const formatDateForRange = (date) => {
    return toUTCDateTimeFormat(date.utc().startOf('hour'));
};

export const getDateRange = (value) => {
    const currentDate = getCurrentDate();

    if (value === 'last_7_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(7, 'days')
                : currentDate.clone().startOf('day').subtract(6, 'days');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'last_14_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(14, 'days')
                : currentDate.clone().startOf('day').subtract(13, 'days');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'monday_today') {
        const startDate = currentDate.clone().startOf('isoWeek');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'month_today') {
        const startDate = currentDate.clone().startOf('month');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'previous_month') {
        const startDate = currentDate.clone().subtract(1, 'month').startOf('month');

        const endDate = currentDate.clone().startOf('month');

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'last_30_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(30, 'days')
                : currentDate.clone().startOf('day').subtract(29, 'days');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'last_90_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(90, 'days')
                : currentDate.clone().startOf('day').subtract(89, 'days');

        if (startDate.isBefore(minAllowedComparisonDate)) {
            return {
                start_date: null,
                end_date: null,
            };
        }
        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    return {
        start_date: null,
        end_date: null,
    };
};

/**
 * @param {Date[]} param
 */
export const findDateRangeOption = ([startDate, endDate]) => {
    const option = controlDateRangeOptions().find(
        (option) => dayjs(startDate).isSame(option.start_date) && dayjs(endDate).isSame(option.end_date)
    );

    if (option) {
        return option;
    }

    return {
        value: 'selected_custom',
        start_date: formatDateForRange(dayjs(startDate)),
        end_date: formatDateForRange(dayjs.min([getCurrentDate(), dayjs(endDate).add(1, 'day')])),
        label: `${shortDate(startDate)} - ${shortDate(endDate)}`,
    };
};

/**
 * This needs to be a function in order to use dayjs in export.
 * @returns {array}
 */
export const controlDateRangeOptions = () => {
    let options = [
        {
            value: 'last_7_days',
            label: 'Last 7 Days',
            ...getDateRange('last_7_days'),
        },
        {
            value: 'monday_today',
            label: 'Monday To Today',
            ...getDateRange('monday_today'),
        },
        {
            value: 'month_today',
            label: 'Current Month',
            ...getDateRange('month_today'),
        },
        {
            value: 'previous_month',
            label: 'Last Month',
            ...getDateRange('previous_month'),
        },
        {
            value: 'last_30_days',
            label: 'Last 30 Days',
            ...getDateRange('last_30_days'),
        },
        {
            value: 'last_90_days',
            label: 'Last 90 Days',
            ...getDateRange('last_90_days'),
        },
        {
            value: 'custom',
            label: 'Custom Period',
        },
    ];

    return options.filter(
        (option) =>
            option.value !== 'last_90_days' ||
            (option.value === 'last_90_days' && option.dates?.start_date && option.dates?.end_date)
    );
};

/**
 *
 * @param {{start,end}|Array} range
 * @returns {string[]}
 */
export const computeComparisonRange = (range) => {
    let start;
    let end;

    if (Array.isArray(range)) {
        start = range[0];
        end = range[1];
    } else {
        start = range.start;
        end = range.end;
    }

    const startDate = dayjs(start).tz(getClientTz());
    const endDate = dayjs(end).tz(getClientTz());

    const daysDiff = Math.ceil(endDate.diff(startDate, 'days', true));

    const comparisonDate = startDate.clone().subtract(daysDiff, 'days');

    if (comparisonDate.isBefore(minAllowedComparisonDate)) {
        return [toUTCDateTimeFormat(startDate), toUTCDateTimeFormat(endDate)];
    }

    return [toUTCDateTimeFormat(comparisonDate), toUTCDateTimeFormat(startDate), toUTCDateTimeFormat(endDate)];
};

export const calculateRangeBetweenTwoDates = ({ startDate, endDate }) => {
    const dates = [];
    const startDayjs = dayjs(startDate).tz(getClientTz());
    const endDayjs = dayjs(endDate).tz(getClientTz());

    let currentDate = startDayjs.clone();

    // first point
    dates.push(toUTCDateTimeFormat(currentDate));

    // remaining points
    while (currentDate.unix() < endDayjs.unix()) {
        currentDate = currentDate.add(1, 'day');
        dates.push(toUTCDateTimeFormat(currentDate));
    }

    // move last point back
    dates[dates.length - 1] = toUTCDateTimeFormat(dayjs(endDate));

    return dates;
};

export const validateRangeDates = (dates) => {
    if (!dates?.length || dates.length < 2 || dates.length > 100) {
        return false;
    }

    return dates.every((date) => dayjs(date, 'Y-m-dTH:00:00Z').isValid());
};

export const metricsInputValidator = (fetchParams) => {
    if (!fetchParams || fetchParams.constructor !== Object) {
        console.warn('Invalid prop passed in metricsInputValidator', fetchParams);
        return false;
    }

    if (!validateRangeDates(fetchParams.range)) {
        console.warn('Invalid range in metricsInputValidator', fetchParams);
        return false;
    }

    if (
        fetchParams?.filters?.resource_types &&
        (!Array.isArray(fetchParams.filters.resource_types) ||
            fetchParams.filters.resource_types.length === 0 ||
            fetchParams.filters.resource_types.length > 5)
    ) {
        console.warn('Invalid filters.resource_types in metricsInputValidator', fetchParams);
        return false;
    }

    if (
        fetchParams?.filters?.audience_types &&
        (!Array.isArray(fetchParams.filters.audience_types) ||
            fetchParams.filters.audience_types.length === 0 ||
            fetchParams.filters.audience_types.length > 4)
    ) {
        console.warn('Invalid filters.audience_types in metricsInputValidator', fetchParams);
        return false;
    }

    if (
        fetchParams?.filters?.resources &&
        (!Array.isArray(fetchParams.filters.resources) ||
            fetchParams.filters.resources.length === 0 ||
            fetchParams.filters.resources.length > 10)
    ) {
        console.warn('Invalid filters.resources in metricsInputValidator', fetchParams);
        return false;
    }

    return true;
};
